import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject, catchError, concatMap, defaultIfEmpty, filter, finalize, from, map, Observable, of, shareReplay, switchMap, take, tap, throwError } from 'rxjs';
import { AgentMode, Chat, ChatHistoryResponse, ChatHistorySession, Contact, Profile } from './ai-chat.types';
import { environment } from 'environments/environment';

@Injectable({ providedIn: 'root' })
export class ChatService {

    public _sessions: WritableSignal<Chat[]> = signal([]);
    public _currentSessionId: WritableSignal<string | null> = signal(null);

    private _chat: BehaviorSubject<Chat> = new BehaviorSubject(null);
    private _chats: BehaviorSubject<Chat[]> = new BehaviorSubject(null);
    private _contact: BehaviorSubject<Contact> = new BehaviorSubject(null);
    private _contacts: BehaviorSubject<Contact[]> = new BehaviorSubject(null);
    private _profile: BehaviorSubject<Profile> = new BehaviorSubject(null);
    private _sessionId: BehaviorSubject<string> = new BehaviorSubject(null);

    // Expose sessions as a readonly computed signal
    get sessions() {
        return this._sessions.asReadonly();
    }

    // Expose current session ID
    get currentSessionId() {
        return this._currentSessionId.asReadonly();
    }

    // Set current session ID (null clears a deleted / abandoned session)
    setCurrentSessionId(sessionId: string | null): void {
        this._currentSessionId.set(sessionId);
    }

    _apiLink = environment.apiLink;

    /** Coalesce concurrent identical history fetches (findSession + conversation bootstrap). */
    private _historyInflight = new Map<string, Observable<ChatHistoryResponse>>();
    /** Coalesce concurrent identical external-history fetches. */
    private _externalHistoryInflight = new Map<string, Observable<ChatHistorySession[]>>();

    constructor(private _httpClient: HttpClient) {
    }

    private _dedupeHistoryRequest(
        key: string,
        factory: () => Observable<ChatHistoryResponse>,
    ): Observable<ChatHistoryResponse> {
        const existing = this._historyInflight.get(key);
        if (existing) {
            return existing;
        }
        const request$ = factory().pipe(
            shareReplay({ bufferSize: 1, refCount: true }),
            finalize(() => this._historyInflight.delete(key)),
        );
        this._historyInflight.set(key, request$);
        return request$;
    }

    private _dedupeExternalHistoryRequest(
        key: string,
        factory: () => Observable<ChatHistorySession[]>,
    ): Observable<ChatHistorySession[]> {
        const existing = this._externalHistoryInflight.get(key);
        if (existing) {
            return existing;
        }
        const request$ = factory().pipe(
            shareReplay({ bufferSize: 1, refCount: true }),
            finalize(() => this._externalHistoryInflight.delete(key)),
        );
        this._externalHistoryInflight.set(key, request$);
        return request$;
    }

    ask(modal) {
        return this._httpClient.post(this._apiLink + 'chat/ask', modal);
    }

    askCustomAgent(modal) {
        return this._httpClient.post(this._apiLink + 'chat/character', modal);
    }

    getHistory(agentId, customAgentId) {
        const key = `${agentId ?? ''}:${customAgentId ?? 0}`;
        return this._dedupeHistoryRequest(key, () => {
            let params = new HttpParams();
            if (agentId) {
                params = params.append('agentId', agentId);
            }
            if (customAgentId) {
                params = params.append('customAgentId', customAgentId);
            }
            return this._httpClient
                .get(this._apiLink + 'ai-chat/history', { params })
                .pipe(
                    map((res: any): ChatHistoryResponse => {
                        const data = res?.data ?? {};
                        return {
                            count: data.count ?? 0,
                            chats: data.chats ?? [],
                            voice_chat: data.voice_chat,
                        };
                    }),
                );
        });
    }

    /**
     * Find which agent owns `sessionId` by walking each agent's history.
     * Stops at the first hit so a pasted `/ai-chat/agent-chat/:sessionId` can
     * select the owning agent instead of rewriting the URL to another thread.
     */
    findSessionInAgentHistories(
        sessionId: string,
        agentEids: string[],
    ): Observable<{ agentEid: string; chats: ChatHistorySession[] } | null> {
        if (!sessionId || !agentEids.length) {
            return of(null);
        }
        return from(agentEids).pipe(
            concatMap(agentEid =>
                this.getHistory(agentEid, 0).pipe(
                    map((res: ChatHistoryResponse) => {
                        const chats = res?.chats ?? [];
                        return chats.some(chat => chat.sessionId === sessionId)
                            ? { agentEid, chats }
                            : null;
                    }),
                    catchError(() => of(null)),
                ),
            ),
            filter(
                (hit): hit is { agentEid: string; chats: ChatHistorySession[] } =>
                    hit !== null,
            ),
            take(1),
            defaultIfEmpty(null),
        );
    }

    getExternalHistory(agentId) {
        const key = String(agentId ?? '');
        return this._dedupeExternalHistoryRequest(key, () => {
            let params = new HttpParams();
            if (agentId) {
                params = params.append('agentId', agentId);
            }
            return this._httpClient
                .get(this._apiLink + 'external/chat/history-agent', { params })
                .pipe(map((res: any) => res.data.chats as ChatHistorySession[]));
        });
    }

    getChatsBySessionId(sessionId) {
        return this._httpClient.get(this._apiLink + `ai-chat/chats/${sessionId}`).pipe(
            map((res: any) => {
                return res.data.messages
            }),
        );
    }

    getSuggestion(modal) {
        return this._httpClient.post(this._apiLink + 'runnables/suggestions', modal).pipe(
            map((res: any) => {
                return res.data
            }),
        );
    }

    getKPI(modal) {
        return this._httpClient.post(this._apiLink + 'runnables/kpi-suggestions-v2', modal).pipe(
            map((res: any) => {
                return res.data
            }),
        );
    }

    getMessageData(sessionId, messageId) {
        return this._httpClient.get(this._apiLink + `ai-chat/message-data/${sessionId}/${messageId}`).pipe(
            map((res: any) => {
                return res.data.message
            }),
        );
    }

    /**
     * PUT /ai-chat/generate-chart - builds and saves the reply's charts on the first click
     * (replies no longer get charts when answered), then returns the same message payload as
     * `getMessageData`. Later calls return the saved charts without rebuilding them.
     */
    generateChart(sessionId: string, messageId: number | string) {
        return this._httpClient
            .put(this._apiLink + 'ai-chat/generate-chart', {
                sessionId,
                messageId: Number(messageId),
            })
            .pipe(map((res: any) => res.data.message));
    }

    /** Full GET response (e.g. shared recipients alongside message payload). */
    getMessageDataFull(sessionId: string, messageId: string) {
        return this._httpClient.get(this._apiLink + `ai-chat/message-data/${sessionId}/${messageId}`);
    }

    /** POST /ai-chat/share-message - matches API: sessionId, messageId, sharedTo* arrays. */
    shareMessage(body: {
        sessionId: string;
        messageId: number;
        sharedToUserIds: number[];
        sharedToMemberIds: number[];
        sharedToStaffIds: number[];
    }) {
        return this._httpClient.post(this._apiLink + 'ai-chat/share-message', body);
    }

    getMessageDataForReport(messageId, ignore?) {
        let params = new HttpParams();
        if (ignore) {
            params = params.append('Ignore', ignore)
        }
        return this._httpClient.get(this._apiLink + `ai-chat/message-data/${messageId}`, { params }).pipe(
            map((res: any) => {
                return res.data.message
            }),
        );
    }

    getQuestionareDataForReport(questionId, ignore?) {
        let params = new HttpParams();
        params = params.append('questionnaireIds', questionId)
        if (ignore) {
            params = params.append('Ignore', ignore)
        }
        return this._httpClient.get(this._apiLink + 'questionaire/get-questionnaire-with-data', { params }).pipe(
            map((res: any) => {
                return res.data.questionnareData
            }),
        );
    }

    queryExplanition(modal) {
        return this._httpClient.post(this._apiLink + 'runnables/query-explain', modal)
    }

    checkMessageHasQueryExplanation(chatId) {
        return this._httpClient.get(this._apiLink + `ai-chat/message-query-explanation/${chatId}`).pipe(
            map((res: any) => {

                return res.data.message
            }))
    }

    newChat(agentId, customAgentId) {
        const modal = {
            agentId: agentId || '',
            customAgentId: customAgentId || ''
        }

        return this._httpClient.post(this._apiLink + 'ai-chat/new-chat', modal).pipe(
            map((res: any) => {
                return res.data.chat
            }),
        );
    }

    getSessionLogs(sessionId: string) {
        return this._httpClient.get<any>(
            this._apiLink + 'logs/session',
            { params: { sessionId } }
        ).pipe(map((res: any) => (res?.data ?? res) as Record<number, any[]>));
    }

    /**
     * The trace of the sub-agent run an `invoke_agent` step started, by the session that step ran in
     * and the step's tool call id. `sessionId` comes back null when no run was logged for it.
     */
    getSubAgentLogs(
        sessionId: string,
        toolCallId: string,
    ): Observable<{ sessionId: string | null; logs: unknown[]; agent: { name: string; image: string | null } | null }> {
        return this._httpClient
            .get<unknown>(this._apiLink + 'logs/sub-agent', { params: { sessionId, toolCallId } })
            .pipe(
                map(res => {
                    const body = (res as { data?: unknown })?.data ?? res;
                    const value = (body ?? {}) as {
                        sessionId?: unknown;
                        logs?: unknown;
                        agent?: { name?: unknown; image?: unknown } | null;
                    };
                    return {
                        sessionId: typeof value.sessionId === 'string' ? value.sessionId : null,
                        logs: Array.isArray(value.logs) ? value.logs : [],
                        agent:
                            typeof value.agent?.name === 'string'
                                ? { name: value.agent.name, image: typeof value.agent.image === 'string' ? value.agent.image : null }
                                : null,
                    };
                }),
            );
    }


    private readonly _agentModeBySession = new Map<string, AgentMode>();

    /** Current conversation's Agent Mode (per-session; default analytics). */
    currentAgentMode(): AgentMode {
        const sid = this.currentSessionId();
        return (sid && this._agentModeBySession.get(sid)) || 'analytics';
    }

    /** Select the Agent Mode for the current conversation. */
    setAgentMode(mode: AgentMode): void {
        const sid = this.currentSessionId();
        if (sid) {
            this._agentModeBySession.set(sid, mode);
        }
    }

    /** Persist Agent Mode server-side; echoes back over `agent-mode-stream` to every connected client. */
    setAgentModePersisted(sessionId: string, mode: AgentMode) {
        return this._httpClient.put(this._apiLink + 'ai-chat/agent-mode', { sessionId, mode });
    }

    sendMessage(modal, offset?, limit?) {
        let params = new HttpParams();
        if (offset) {
            params = params.append('offset', offset)
        }
        if (limit) {
            params = params.append('limit', limit)
        }
        return this._httpClient.post(this._apiLink + 'ai-chat/send-message', modal, { params })
    }

    answerQuestions(body: {
        sessionId: string;
        questionMessageId: number;
        answers: { id: string; answer: string }[];
        limit?: number;
        offset?: number;
        mode?: AgentMode;
    }) {
        return this._httpClient.post(
            this._apiLink + 'ai-chat/answer-questions',
            body,
        );
    }

    getQuestionOptions(params: {
        sessionId: string;
        agentId: string;
        column: string;
        table: string;
    }) {
        const httpParams = new HttpParams()
            .set('sessionId', params.sessionId)
            .set('agentId', params.agentId)
            .set('column', params.column)
            .set('table', params.table);
        return this._httpClient.get(
            this._apiLink + 'ai-chat/question-options',
            { params: httpParams },
        );
    }

    markAsFavorite(modal) {
        return this._httpClient.put(this._apiLink + 'ai-chat/mark-as-favorite', modal)
    }


    markChartAsFavorite(modal) {
        return this._httpClient.put(this._apiLink + 'ai-chat/mark-as-favorite-chart', modal)
    }


    markAsIcorrect(modal) {
        return this._httpClient.put(this._apiLink + 'ai-chat/mark-as-correct', modal)
    }

    regenerate(modal) {
        return this._httpClient.put(this._apiLink + 'ai-chat/regenerate', modal)
    }

    editMessage(body: { sessionId: string; messageId: number; newPrompt: string; mode?: AgentMode }) {
        return this._httpClient.post(this._apiLink + 'ai-chat/edit-message', body);
    }

    switchBranch(body: { sessionId: string; messageId: number; targetSiblingId: number }) {
        return this._httpClient.put(this._apiLink + 'ai-chat/switch-branch', body);
    }

    deleteChat(sessionId) {
        return this._httpClient.put(this._apiLink + `ai-chat/delete-chat/${sessionId}`, {})
    }

    cancelResponseGeneration(sessionId: string) {
        return this._httpClient.post(this._apiLink + 'ai-chat/cancel-response-generation', { sessionId });
    }

    /** POST /ai-chat/extract-query-metadata - table, date columns, and chart type by query id. */
    extractQueryMetadata(body: { queryId: string | number; dbType: string }) {
        return this._httpClient.post(
            this._apiLink + 'ai-chat/extract-query-metadata',
            body
        );
    }


    get chat$(): Observable<Chat> {
        return this._chat.asObservable();
    }


    get chats$(): Observable<Chat[]> {
        return this._chats.asObservable();
    }

    get contact$(): Observable<Contact> {
        return this._contact.asObservable();
    }


    get contacts$(): Observable<Contact[]> {
        return this._contacts.asObservable();
    }


    get profile$(): Observable<Profile> {
        return this._profile.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get chats
     */
    getChats(): Observable<any> {
        return this._httpClient.get<Chat[]>('api/apps/chat/chats').pipe(
            tap((response: Chat[]) => {
                this._chats.next(response);
            }),
        );
    }

    /**
     * Get contact
     *
     * @param id
     */
    getContact(id: string): Observable<any> {
        return this._httpClient.get<Contact>('api/apps/chat/contacts', { params: { id } }).pipe(
            tap((response: Contact) => {
                this._contact.next(response);
            }),
        );
    }

    /**
     * Get contacts
     */
    getContacts(): Observable<any> {
        return this._httpClient.get<Contact[]>('api/apps/chat/contacts').pipe(
            tap((response: Contact[]) => {
                this._contacts.next(response);
            }),
        );
    }

    /**
     * Get profile
     */
    getProfile(): Observable<any> {
        return this._httpClient.get<Profile>('api/apps/chat/profile').pipe(
            tap((response: Profile) => {
                this._profile.next(response);
            }),
        );
    }

    /**
     * Get chat
     *
     * @param id
     */
    getChatById(id: string): Observable<any> {
        return this._httpClient.get<Chat>('api/apps/chat/chat', { params: { id } }).pipe(
            map((chat) => {
                // Update the chat
                this._chat.next(chat);

                // Return the chat
                return chat;
            }),
            switchMap((chat) => {
                if (!chat) {
                    return throwError('Could not found chat with id of ' + id + '!');
                }

                return of(chat);
            }),
        );
    }


    updateChat(id: string, chat: Chat): Observable<Chat> {
        return this.chats$.pipe(
            take(1),
            switchMap(chats => this._httpClient.patch<Chat>('api/apps/chat/chat', {
                id,
                chat,
            }).pipe(
                map((updatedChat) => {
                    // Find the index of the updated chat
                    const index = chats.findIndex(item => item.id === id);

                    // Update the chat
                    chats[index] = updatedChat;

                    // Update the chats
                    this._chats.next(chats);

                    // Return the updated contact
                    return updatedChat;
                }),
                switchMap(updatedChat => this.chat$.pipe(
                    take(1),
                    filter(item => item && item.id === id),
                    tap(() => {
                        // Update the chat if it's selected
                        this._chat.next(updatedChat);

                        // Return the updated chat
                        return updatedChat;
                    }),
                )),
            )),
        );
    }

    /**
     * Reset the selected chat
     */
    resetChat(): void {
        this._chat.next(null);
    }

    tuneUserPrompt(modal) {
        return this._httpClient.post(this._apiLink + 'agents/tune-user-prompt', modal)
    }
}
